import { useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, Crop, Loader2, Maximize, RotateCcw, Trash2, Upload } from "lucide-react";
import { formatApiError } from "@/lib/api";
import { sizeLabel } from "@/lib/signs";
import { uploadArtwork } from "@/components/signs/useSignConfig";

const CHECKER = {
    backgroundImage: "linear-gradient(45deg, #e4e4e7 25%, transparent 25%), linear-gradient(-45deg, #e4e4e7 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e4e4e7 75%), linear-gradient(-45deg, transparent 75%, #e4e4e7 75%)",
    backgroundSize: "16px 16px", backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0",
};

function Fit({ slot, side, cfg, theme }) {
    const t = slot.transform;
    const seg = (fit, Icon, label, testid) => (
        <button type="button" onClick={() => cfg.setTransform(side, { ...t, fit })} data-testid={testid}
            className="inline-flex flex-1 items-center justify-center gap-1 rounded-md px-2 py-1 text-[11px] font-bold transition-colors"
            style={(t.fit || "cover") === fit ? { background: theme?.primary, color: theme?.on } : { color: "#52525b" }}>
            <Icon size={12} /> {label}
        </button>
    );
    return (
        <div className="mt-2 space-y-2">
            <div className="flex items-center gap-1 rounded-lg bg-zinc-100 p-0.5">
                {seg("cover", Maximize, "Fill", `fit-fill-${side}`)}
                {seg("contain", Crop, "Fit", `fit-contain-${side}`)}
                <button type="button" onClick={() => cfg.setTransform(side, { x: 0, y: 0, zoom: 1, fit: t.fit })} data-testid={`art-reset-${side}`}
                    className="rounded-md px-2 py-1 text-zinc-500 transition-colors hover:bg-white hover:text-zinc-900" title="Reset position">
                    <RotateCcw size={12} />
                </button>
            </div>
            <label className="flex items-center gap-2 text-[11px] text-zinc-500">
                <span className="font-mono uppercase tracking-widest">Zoom</span>
                <input type="range" min="0.3" max="3" step="0.02" value={t.zoom} data-testid={`art-zoom-${side}`}
                    onChange={(e) => cfg.setTransform(side, { ...t, zoom: Number(e.target.value) })}
                    className="flex-1" style={{ accentColor: theme?.primary }} />
                <span className="w-9 text-right font-mono">{Math.round(t.zoom * 100)}%</span>
            </label>
        </div>
    );
}

/** One side of the sign: pick a file, see it, fit it. */
function ImageSlot({ side, cfg, theme, active }) {
    const slot = cfg.sides[side];
    const fileRef = useRef(null);
    const [over, setOver] = useState(false);
    const label = side === "front" ? "Front image" : "Back image";

    const pick = async (file) => {
        if (!file) return;
        const url = file.type.startsWith("image/") ? URL.createObjectURL(file) : null;
        cfg.previewImage(side, url);
        cfg.setView(side);
        try {
            const rec = await uploadArtwork(file);
            cfg.attachImage(side, rec, url);
            toast.success(`${label} uploaded`);
        } catch (e) {
            cfg.clearImage(side);
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const f = slot.file;
    return (
        <div className={`rounded-xl border bg-white p-2.5 transition-colors ${active ? "shadow-sm" : "border-zinc-200"}`}
            style={active ? { borderColor: theme?.primary } : undefined} data-testid={`image-slot-${side}`}
            onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
            onDrop={(e) => { e.preventDefault(); setOver(false); pick(e.dataTransfer.files?.[0]); }}>
            <button type="button" onClick={() => cfg.setView(side)} className="flex w-full items-center justify-between" data-testid={`view-${side}`}>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</span>
                {active && <span className="h-1.5 w-1.5 rounded-full" style={{ background: theme?.primary }} />}
            </button>

            {slot.url || slot.busy ? (
                <>
                    <div className="relative mt-2 h-28 overflow-hidden rounded-lg border border-zinc-200" style={CHECKER}>
                        {slot.url && <img src={slot.url} alt={label} className="h-full w-full object-contain" data-testid={`thumb-${side}`} />}
                        {slot.busy && <span className="absolute inset-0 grid place-items-center bg-white/60"><Loader2 className="animate-spin" size={18} style={{ color: theme?.primary }} /></span>}
                    </div>
                    {f && (
                        <p className="mt-1.5 truncate text-[11px] text-zinc-700" title={f.filename} data-testid={`filename-${side}`}>{f.filename}</p>
                    )}
                    {f?.width_px && (
                        <p className="font-mono text-[10px] text-zinc-500" data-testid={`filesize-${side}`}>
                            {f.width_px} × {f.height_px} px · {sizeLabel(f.width_in, f.height_in)} at {f.dpi} dpi
                        </p>
                    )}
                    <div className="mt-1.5 flex gap-3 text-[11px] font-semibold">
                        <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1 text-zinc-700 hover:underline" data-testid={`replace-${side}`}><Upload size={11} /> Replace</button>
                        <button type="button" onClick={() => cfg.clearImage(side)} className="inline-flex items-center gap-1 text-zinc-500 hover:text-red-600" data-testid={`remove-${side}`}><Trash2 size={11} /> Remove</button>
                    </div>
                    {slot.url && <Fit slot={slot} side={side} cfg={cfg} theme={theme} />}
                </>
            ) : (
                <>
                    <button type="button" onClick={() => fileRef.current?.click()} data-testid={`artwork-upload-btn-${side}`}
                        className={`mt-2 flex w-full flex-col items-center gap-1 rounded-lg border-2 border-dashed px-3 py-6 text-center transition-colors ${over ? "bg-zinc-50" : "border-zinc-300 hover:bg-zinc-50"}`}
                        style={over ? { borderColor: theme?.primary } : undefined}>
                        <Upload size={18} style={{ color: theme?.primary }} />
                        <span className="text-xs font-bold text-zinc-800">Select {side} image</span>
                        <span className="text-[11px] text-zinc-500">or drop it here</span>
                    </button>
                    {side === "back" && cfg.sides.front.url && (
                        <button type="button" onClick={cfg.copyFrontToBack} data-testid="use-front-image"
                            className="mt-1.5 inline-flex w-full items-center justify-center gap-1 rounded-lg border border-zinc-200 py-1.5 text-[11px] font-semibold text-zinc-700 hover:bg-zinc-50">
                            <Copy size={11} /> Use the front image
                        </button>
                    )}
                </>
            )}
            <input ref={fileRef} type="file" className="hidden" accept="image/*,.pdf,.ai,.eps,.svg,.psd"
                onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} data-testid={`artwork-file-input-${side}`} />
        </div>
    );
}

/** The left rail: nothing but the images that go on the sign. */
export default function ImageRail({ cfg, doubleSided, theme, className = "" }) {
    const list = doubleSided ? ["front", "back"] : ["front"];
    return (
        <aside className={`flex flex-col gap-3 overflow-y-auto bg-white p-3 ${className}`} data-testid="image-rail">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Images</p>
            {list.map((side) => <ImageSlot key={side} side={side} cfg={cfg} theme={theme} active={cfg.view === side} />)}
            <p className="mt-auto text-[11px] leading-snug text-zinc-400">
                PNG, JPG, PDF, AI, EPS or SVG. The size fills in from your file. Drag the image on the sign to move it.
            </p>
        </aside>
    );
}
